import React, { useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  CUSTOMER_BRAND,
  LEGAL_ENTITY,
  BRAND_ATTRIBUTION,
  STATEMENT_DESCRIPTOR,
  PAYMENT_SURFACES,
  BRAND_ACTION_ORDER,
  type BrandVerdict,
} from '@/data/paymentBrand';

const verdictStyle: Record<BrandVerdict, { label: string; cls: string }> = {
  rebrand: { label: 'Rebrand to GIGGONE', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rebrand_with_disclosure: {
    label: 'GIGGONE + LLC disclosure',
    cls: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  legal_entity_required: {
    label: 'Must stay Sky Is The Limit Shop LLC',
    cls: 'bg-amber-100 text-amber-900 border-amber-200',
  },
};

/**
 * Admin > Payments > Branding & acceptance.
 *
 * Two jobs in one panel:
 *  1. The branding matrix — every customer-facing payment surface with a
 *     verdict (rebrand / rebrand with disclosure / legal entity required).
 *  2. A one-click end-to-end acceptance run against the live webhook, proving
 *     payment event -> payment -> subscription -> credits without a card.
 */
const AdminPaymentBranding: React.FC = () => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string>('');

  const runAcceptance = async () => {
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('payment-acceptance-test', { body: {} });
      if (fnError) throw new Error(fnError.message);
      setResult(data);
    } catch (e) {
      setError((e as Error)?.message || 'Acceptance test could not be reached.');
    } finally {
      setRunning(false);
    }
  };

  const summary = result?.summary || null;
  const chip = (state: string) =>
    state === 'PASS'
      ? 'bg-emerald-100 text-emerald-800'
      : state === 'FAIL'
      ? 'bg-red-100 text-red-800'
      : 'bg-slate-100 text-slate-700';

  return (
    <section className="mt-10 space-y-8">
      {/* ---------------- Acceptance test ---------------- */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Payment pipeline acceptance test</h3>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              Posts real signed deliveries to the live webhook with all four required headers, then reads back the
              rows created: payment event, payment record, subscription record and credit balance. Uses a synthetic
              contractor on a reserved <code className="rounded bg-slate-100 px-1">.invalid</code> address and deletes
              it afterwards. No card is charged.
            </p>
          </div>
          <button
            type="button"
            onClick={runAcceptance}
            disabled={running}
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
          >
            {running ? 'Running…' : 'Run acceptance test'}
          </button>
        </div>

        {error && (
          <p className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>
        )}

        {summary && (
          <div className="mt-5 space-y-4">
            <div className="flex flex-wrap gap-2">
              {Object.entries(summary).map(([k, v]) => (
                <span key={k} className={`rounded-full px-3 py-1 text-xs font-semibold ${chip(String(v))}`}>
                  {k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())}: {String(v)}
                </span>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-2">Plan</th>
                    <th className="py-2">HTTP</th>
                    <th className="py-2">Result</th>
                    <th className="py-2">Event</th>
                    <th className="py-2">Payment</th>
                    <th className="py-2">Subscription</th>
                    <th className="py-2">Credits</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(result?.membership || []).map((m: any) => (
                    <tr key={m.plan}>
                      <td className="py-2 font-medium capitalize text-slate-900">{m.plan}</td>
                      <td className="py-2 text-slate-600">{m.httpStatus}</td>
                      <td className="py-2 text-slate-600">{m.webhookResult}</td>
                      <td className="py-2">{m.paymentEventCreated ? 'created' : '—'}</td>
                      <td className="py-2">{m.paymentRecordCreated ? 'created' : '—'}</td>
                      <td className="py-2">{m.subscriptionRecordCreated ? 'created' : '—'}</td>
                      <td className="py-2 font-semibold text-slate-900">
                        {m.creditsBefore} → {m.creditsAfter}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {result?.crmDeliveries && (
              <p
                className={`rounded-lg border p-3 text-sm ${
                  result.crmDeliveries.everReceivedRealDelivery
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                    : 'border-amber-200 bg-amber-50 text-amber-900'
                }`}
              >
                {result.crmDeliveries.verdict}
              </p>
            )}

            {result?.withoutPlatformTokenHeaders && (
              <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                Control case — delivery sent with only <code>x-webhook-secret</code>: HTTP{' '}
                {result.withoutPlatformTokenHeaders.httpStatus}. {result.withoutPlatformTokenHeaders.meaning}
              </p>
            )}
          </div>
        )}
      </div>

      {/* ---------------- Branding matrix ---------------- */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900">Customer-facing payment branding</h3>
        <p className="mt-1 text-sm text-slate-600">
          Customer brand <strong>{CUSTOMER_BRAND}</strong> · Legal entity <strong>{LEGAL_ENTITY}</strong>. Use{' '}
          <span className="font-medium">“{BRAND_ATTRIBUTION}”</span> wherever both must appear. Card statement
          descriptor: <code className="rounded bg-slate-100 px-1">{STATEMENT_DESCRIPTOR}</code>.
        </p>

        <ol className="mt-5 space-y-2 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          {BRAND_ACTION_ORDER.map((step, i) => (
            <li key={i} className="flex gap-2">
              <span className="font-semibold">{i + 1}.</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>

        <div className="mt-6 space-y-3">
          {PAYMENT_SURFACES.map((s) => {
            const v = verdictStyle[s.verdict];
            return (
              <div key={s.surface} className="rounded-xl border border-slate-200 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <h4 className="font-semibold text-slate-900">{s.surface}</h4>
                  <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${v.cls}`}>{v.label}</span>
                </div>
                <p className="mt-2 text-xs uppercase tracking-wide text-slate-500">{s.where}</p>
                <p className="mt-2 text-sm text-slate-800">
                  <span className="font-medium">Set to:</span> {s.setTo}
                </p>
                <p className="mt-1 text-sm text-slate-600">{s.note}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AdminPaymentBranding;
